import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Button, CopyButton, Icon, Text } from '@perch-labs/shrike';
import { useConnect } from '@arcadia-eng/connect-react';
import { copyFor, routes } from 'config';
import { Container } from 'styles';
import { GET_ACCOUNT_NOTIFICATIONS } from 'hooks/use-account-notifications.hook';
import { trackEvent } from 'domain/tracking';
import {
  GET_UTILITY_CONNECT_TOKEN,
  GET_UTILITY_SYNC_DATA,
} from '../../utility-sync.api';
import { ErrorText } from './utility-credential-connect-button.style';

const getCopy = copyFor('settings.utilitySync');

interface Props {
  accountId: number;
  language?: string;
  userId: number;
  utilityCredentialId: number;
  userName: string | null;
  movingUser?: boolean;
}

export const UtilityCredentialConnectButton = (props: Props) => {
  const location: { state: { showUtilityResyncPopup: false } } = useLocation();
  const [connectIsOpen, setConnectIsOpen] = useState(false);
  const history = useHistory();
  const {
    userId,
    utilityCredentialId,
    accountId,
    userName,
    language,
    movingUser,
  } = props;
  const {
    data: connectTokenData,
    loading: connectTokenLoading,
    error: connectTokenError,
    refetch: refetchConnectToken,
  } = useQuery<
    GetUtilityConnectTokenQuery,
    GetUtilityConnectTokenQueryVariables
  >(GET_UTILITY_CONNECT_TOKEN, {
    fetchPolicy: 'no-cache',
    skip: !utilityCredentialId,
    variables: { utilityCredentialId },
  });

  const [{ loading: connectLoading, error: connectError }, open] = useConnect();
  const [error, setError] = useState(connectError);
  const [hasRefetched, setHasRefetched] = useState(false);
  const loading = connectTokenLoading || connectLoading;
  useEffect(() => {
    if (connectTokenError) {
      const handleRefetch = async () => {
        try {
          await refetchConnectToken();
        } catch (err) {
          setError(err);
        }
      };

      if (!hasRefetched) {
        handleRefetch();
        setHasRefetched(true);
      }
    }
  }, [connectTokenError, hasRefetched]);

  useEffect(() => {
    if (connectTokenData) {
      setError(connectError);
    }
  }, [connectTokenData, connectError]);

  const [getUtilitySyncData] = useLazyQuery<
    GetUtilitySyncDataQuery,
    GetUtilitySyncDataQueryVariables
  >(GET_UTILITY_SYNC_DATA, { fetchPolicy: 'network-only' });

  const [getAccountNotificationData] = useLazyQuery<
    GetAccountNotificationsQuery,
    GetAccountNotificationsQueryVariables
  >(GET_ACCOUNT_NOTIFICATIONS, { fetchPolicy: 'network-only' });

  const onClose = useCallback(
    ({ status }) => {
      if (!['no_submit', 'error'].includes(status)) {
        getUtilitySyncData({ variables: { accountId } });
        getAccountNotificationData({ variables: { id: accountId } });
        // Redirect moving user directly to the dashboard on successful connect
        if (movingUser) {
          const utmSource = localStorage.getItem('utmSource');
          const utmMedium = localStorage.getItem('utmMedium');
          const utmCampaign = localStorage.getItem('utmCampaign');
          trackEvent('moving_utility_connected_successfully', {
            metadata: {
              accountId,
              userId,
              utmSource,
              utmMedium,
              utmCampaign,
            },
          });
          history.push(routes().dashboard);
        }
      }
    },
    [getUtilitySyncData, userId, accountId, getAccountNotificationData]
  );

  const onError = useCallback(({ error }) => {
    Sentry.captureException(
      new Error(
        `[Utility Connect Error]: Message: ${error.message}, Stack: ${error.stack}`
      )
    );
  }, []);

  const onOpen = useCallback(() => {
    setConnectIsOpen(true);
  }, []);

  const config = {
    _experimental: {
      cannotChangeUtility: true,
      language,
      showArcadiaSupport: true,
    },
    callbacks: { onClose, onError, onOpen },
    connectToken: connectTokenData?.utilityCredentialConnectToken,
    env: process.env.ARCADIA_UTILITY_CONNECT_ENV,
  };

  useEffect(() => {
    if (
      !connectIsOpen &&
      loading === false &&
      location.state &&
      location.state.showUtilityResyncPopup
    ) {
      open(config);
    }
  }, [location.state, loading, connectIsOpen]);

  return (
    <div>
      {movingUser ? (
        <>
          <Container flex margin={{ top: '16px' }}>
            <Button
              aria-label={getCopy('connectUtilityAriaLabel')}
              loading={loading}
              disabled={!!error}
              onClick={() => open(config)}
              backgroundColor="primaryInverse"
              fullWidth
            >
              <Icon
                margin={{ right: '8px' }}
                color="primaryInverse"
                icon="Lock"
              />
              <Text textStyle="paragraph500" color="primaryInverse">
                {getCopy('connectUtilityButton')}
              </Text>
            </Button>
          </Container>
          <Container flex>
            {error && (
              <ErrorText
                textStyle="paragraph400"
                color="error"
                margin={{ bottom: '8px', top: '8px' }}
              >
                {getCopy('reconnectError')}
              </ErrorText>
            )}
          </Container>
        </>
      ) : (
        <Container
          flex
          direction="column"
          alignItems="flex-end"
          xsmall={{ alignItems: 'flex-start' }}
        >
          <Container>
            {userName && (
              <CopyButton
                aria-label={getCopy('copyAriaLabel')}
                copyText={userName}
                backgroundless
                size="small"
                margin={{ right: '8px' }}
              />
            )}
            <Button
              aria-label={getCopy('reconnectAriaLabel')}
              loading={loading}
              disabled={!!error}
              onClick={() => open(config)}
              backgroundless={true}
              size="small"
            >
              {getCopy('reconnectButton')}
            </Button>
          </Container>
          {error && (
            <ErrorText
              textStyle="paragraph400"
              color="error"
              margin={{ bottom: '8px', top: '8px' }}
            >
              {getCopy('reconnectError')}
            </ErrorText>
          )}
        </Container>
      )}
    </div>
  );
};
